import { useUnmount, χref } from '@studio/react-utils/hooks';
import type { T_Maybe, T_Nullable } from '@tryiris-ai/js-types';
import { useEffect, useRef } from 'react';
import type { ReactElement } from 'react';
import { MessageId } from './constants.js';
import { hide, post } from '#~/notifications/index.js';
import { useDepot } from '#~/depot/hooks.js';

/** Network `<Watchdog>` component. */
export const Watchdog = (): T_Nullable<ReactElement> => {
  const isOnline = useDepot(({ network }) => network.isOnline);
  const previousRef = useRef<T_Maybe<boolean>>(undefined);

  useEffect(() => {
    // Clear the previous message, if applicable.
    hide(MessageId);

    if (isOnline) {
      // We are online, but were we just offline?
      if (χref(previousRef) === false) {
        post({
          id: MessageId,
          message: 'The studio has reestablished its internet connection.',
          severity: 'success',
          title: 'Internet Connected'
        });
      }
    } else {
      // We are offline :( This clears itself when the connection returns, so
      // there is nothing for the user to do — it must not light the bell.
      post(
        {
          id: MessageId,
          message:
            'The studio has lost connection to the internet! We will automatically reconnect once your internet connection is restored.',
          severity: 'error',
          title: 'No Internet Connection'
        },
        { closeable: false }
      );
    }

    // Keep track of the previous status.
    previousRef.current = isOnline;
  }, [isOnline]);

  useUnmount(() => {
    hide(MessageId);

    // Play nice with React's `StrictMode`.
    previousRef.current = undefined;
  });

  if (isOnline) {
    return null;
  }

  return null;
  /*
  return (
    <Centered className='z-60 bg-black/50'>
      <Badge message='Internet connection lost. Attempting to reconnect...' severity='error' />
    </Centered>
  );
  */
};
